import * as _ from 'lodash';
import { useState } from 'react';
import ReactTable from 'react-table';
import store from 'store';
import type { FunctionComponent } from 'react';
import type { Column, ComponentPropsGetterR, SortingRule } from 'react-table';

import useStyledColumns from './useStyledColumns';

import styles from './SimpleReactTable.module.less';

export const MINIMUM_PAGE_SIZE = 20;

const getTdProps = _.constant({
  className: styles.tableData,
});

const noopGetter = _.constant({});

type SimpleReactTableSortingProps = {
  isSortable: true;
  onSortedChanged?(sortMode: SortingRule): void;
  defaultSortMode: SortingRule[];
} | {
  isSortable?: boolean;
  onSortedChanged?: never;
  defaultSortMode?: never;
};

export type SimpleReactTableProps = SimpleReactTableSortingProps & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
  columns: Column[];
  showPagination?: boolean;
  cacheId?: string; // provided string to save certain values on the table to browser cache
  getTrProps?: ComponentPropsGetterR;
  minRows?: number;
  getTrGroupProps?: ComponentPropsGetterR;
  isLoading?: boolean;
  pageSize?: number; // controlled page size prop
};

// right now, we only are caching pageSize, but this can eventually be extended to other fields as well
const getStorageIdForField = (cacheId: string, field: 'pageSize') => `${cacheId}_${field}`;

/**
 * SimpleReactTable is a scaffold for a straight-forward React table component.
 * If a `pageSizeIdToSave` prop is provided, then we will save the page size
 * selected by the user to the local browser cache.
 */
const SimpleReactTable: FunctionComponent<SimpleReactTableProps> = ({
  isSortable = true, // Default to true if not provided
  ...props
}) => {
  const [page, setPage] = useState(0);
  const [internalPageSize, setInternalPageSize] = useState(() => {
    if (props.cacheId) {
      return Math.max(
        store.get(getStorageIdForField(props.cacheId, 'pageSize')) || MINIMUM_PAGE_SIZE,
        MINIMUM_PAGE_SIZE,
      );
    }

    return MINIMUM_PAGE_SIZE;
  });

  // Use controlled pageSize if provided, otherwise use internal state
  const pageSize = props.pageSize ?? internalPageSize;

  const onPageSizeChange = (newPageSize: number, newPage: number) => {
    // Only update internal state if pageSize is not controlled
    if (props.pageSize === undefined) {
      setInternalPageSize(newPageSize);
    }
    setPage(newPage);

    // store in browser cache if pageSizeIdToSave provided
    if (props.cacheId) {
      store.set(getStorageIdForField(props.cacheId, 'pageSize'), newPageSize);
    }
  };

  // Apply styles based on attributes
  const styledColumns = useStyledColumns(props.columns);
  const onSortedChanged = props.onSortedChanged ? props.onSortedChanged : () => {};

  return (
    <ReactTable
      className="simple-react-table"
      data={props.data}
      columns={styledColumns}
      minRows={props.minRows ?? 5}
      collapseOnDataChange={false}
      collapseOnSortingChange={false}
      onSortedChange={newSortMode => onSortedChanged(newSortMode[0] as SortingRule)}
      defaultSorted={props.defaultSortMode}
      showPagination={props.showPagination || false}
      pageSizeOptions={[MINIMUM_PAGE_SIZE, 50, 100]}
      pageSize={pageSize}
      page={page}
      onPageSizeChange={onPageSizeChange}
      onPageChange={setPage}
      getTdProps={getTdProps}
      getTrProps={(props.getTrProps || noopGetter) as ComponentPropsGetterR}
      getTrGroupProps={(props.getTrGroupProps || noopGetter) as ComponentPropsGetterR}
      sortable={isSortable}
      loading={props.isLoading}
    />
  );
};

export default SimpleReactTable;
